import React, { useCallback, useMemo, useRef, useState } from "react";
import { addMilliseconds, subMinutes } from "date-fns";
import { observer } from "mobx-react";

import { ICamera } from "@formant/core/model/ICamera";
import { Uuid } from "@formant/core/model/Uuid";

import { useStores } from "../../context/StoreContext";
import { useWebglContext } from "../../utils/webglCheck";
import { DeviceSelector } from "../common/DeviceSelector";
import { DeviceTags } from "../common/DeviceTags";
import { WebglError } from "../common/WebglError";
import { Lazy } from "../Lazy";
import { IBaseModuleProps } from "./IBaseModuleProps";
import * as styles from "./LocalizationModule.scss";
import { Module } from "./Module";
import { ModuleFooter } from "./ModuleFooter";
import { findConfiguration } from "./utils/findConfiguration";
import { pointsByDevice } from "./utils/pointsByDevice";

const ViewerView = Lazy(async () => (await import("./ViewerView")).ViewerView);
ViewerView.displayName = "Lazy(ViewerView)";

function useRecentRequestedData<T>(
    getter: () => T[] | "too much data" | undefined,
): {
    data: T[];
    loading: boolean;
    tooMuchData: boolean;
} {
    const recent = useRef<T[]>([]);
    const queryData = getter();

    const loading = queryData === undefined;
    const tooMuchData = queryData === "too much data";

    const data = !tooMuchData && !loading ? queryData : recent.current;
    recent.current = data;

    return {
        data,
        loading,
        tooMuchData,
    };
}

export const LocalizationModule = observer(function LocalizationModule(
    props: IBaseModuleProps,
) {
    const {
        configuration,
        streamName,
        filter,
        focused,
        hovering,
        fullscreen,
        onChangeConfiguration,
    } = props;
    const hasWebglContext = useWebglContext();

    const { camera } = useMemo(
        () =>
            findConfiguration(configuration, streamName, "localization") || {},
        [configuration, streamName],
    );

    const [selectedDeviceId, setSelectedDeviceId] = useState<Uuid | undefined>(
        undefined,
    );

    const onChangeCamera = useCallback(
        (newCamera: ICamera | undefined) => {
            onChangeConfiguration?.({
                streamName,
                type: "localization",
                updates: { camera: newCamera },
            });
        },
        [onChangeConfiguration],
    );

    const { observeStore, queryStore } = useStores();
    const { time } = observeStore;
    const startTime = subMinutes(time, 1);
    const endTime = addMilliseconds(time, 1);

    const { data, loading, tooMuchData } = useRecentRequestedData(() =>
        // TODO moduleQuery always returns a new reference-array
        // because of the .filterDataByType(). This makes downstream
        // memoization problematic.
        queryStore.moduleQuery(
            filter,
            streamName,
            "localization",
            startTime,
            endTime,
        ),
    );

    const empty = data.length === 0;
    const { deviceIds, localizations, current } = useMemo(() => {
        const deviceData = pointsByDevice(data);
        const active = deviceData.find(it => selectedDeviceId === it.deviceId);
        return {
            deviceIds: data.map(it => it.deviceId),
            localizations: active?.points.map(_ => _.value) || [],
            current: active?.points?.slice(-1)?.[0],
        };
    }, [data, selectedDeviceId]);

    return (
        <Module
            {...props}
            type="localization"
            loading={loading}
            empty={empty}
            tooMuchData={tooMuchData}
            className={styles["localization-module"]}
        >
            <div className={styles.content}>
                {hasWebglContext && (
                    <ViewerView
                        className={styles.view}
                        unfocused={!focused}
                        localizations={localizations}
                        camera={camera}
                        onChangeCamera={onChangeCamera}
                        deviceId={selectedDeviceId}
                        disableUrdf={true}
                    />
                )}
                <WebglError visible={hasWebglContext === false} />
            </div>
            <ModuleFooter visible={hovering} floating={true}>
                {current && selectedDeviceId && (
                    <DeviceTags
                        deviceId={selectedDeviceId}
                        tags={current.tags}
                        time={current.time}
                    />
                )}
                <DeviceSelector
                    selected={selectedDeviceId}
                    deviceIds={deviceIds}
                    onSelect={setSelectedDeviceId}
                    fullscreen={fullscreen}
                />
            </ModuleFooter>
        </Module>
    );
});
